/**
 * Keeps `requestAnimationFrame` callbacks running while the document is
 * hidden.
 *
 * A browser fires no animation frames in a hidden document — there is nothing
 * to paint — so any library that mounts a thing closed and opens it on the
 * NEXT frame stalls there forever. overlay-kit does exactly that: every
 * overlay mounts with `isOpen: false` and is flipped open inside a
 * `requestAnimationFrame`, which is how it gets an enter animation. On a
 * hidden page that frame never comes, so `overlay.open()` resolves to a
 * component that mounts and then sits closed, and NO modal in the app can be
 * opened — not the confirm dialog, not the command palette, not one of the
 * thirty-odd `openXModal()` helpers.
 *
 * This is invisible to someone sitting in front of the tab, which is why it
 * has survived: `document.hidden` is false whenever a human is clicking. It
 * is total for anything driving the app in a headless or occluded window,
 * where every modal silently does nothing — the click lands, the handler
 * runs, and the dialog never arrives.
 *
 * It is reachable by hand too, if rarely: open a modal and switch tabs inside
 * the same frame and you come back to a page that swallowed the click.
 *
 * The shim is inert while the page is visible — the native path is used
 * unchanged, so real animation frames stay tied to real frames. It only
 * stands in while `document.hidden`, where the alternative is not a coarser
 * frame but no frame at all.
 */

/**
 * Roughly one frame at 60Hz. The exact spacing does not matter — nothing is
 * being painted — only that the callback runs at all.
 */
const FRAME_MS = 16

/**
 * Handles handed out by the shim start well above anything a browser will
 * mint for a real frame request, so `cancelAnimationFrame` can tell the two
 * apart without a second lookup table for native ids.
 */
const HANDLE_BASE = 1_000_000_000

let installed = false

export const installHiddenFrameFallback = (): void => {
  if (installed || typeof window === 'undefined') {
    return
  }
  installed = true

  const nativeRequest = window.requestAnimationFrame.bind(window)
  const nativeCancel = window.cancelAnimationFrame.bind(window)
  const pending = new Map<number, ReturnType<typeof setTimeout>>()
  let nextHandle = HANDLE_BASE

  window.requestAnimationFrame = (callback: FrameRequestCallback): number => {
    if (!document.hidden) {
      return nativeRequest(callback)
    }
    nextHandle += 1
    const handle = nextHandle
    pending.set(
      handle,
      setTimeout(() => {
        pending.delete(handle)
        callback(performance.now())
      }, FRAME_MS)
    )
    return handle
  }

  window.cancelAnimationFrame = (handle: number): void => {
    const timer = pending.get(handle)
    if (timer === undefined) {
      nativeCancel(handle)
      return
    }
    clearTimeout(timer)
    pending.delete(handle)
  }
}
